import React, { useState, useEffect } from "react";
import TitlePage from "../../Shared/TitlePage";
import { dataService } from "../../../Services/data/data.service";
import { Grid2, Box } from "@mui/material";
import { makeStyles } from "@material-ui/core/styles";
import { ExternalApiTypes } from "../../../Config/constants";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Button from "../../Shared/Buttons/Button";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../Shared/Select";
import TableExternalApi from "./Components/TableExternalApi";
import TablePagination from "@mui/material/TablePagination";

function ExternalApiOverviewPage(props) {
  const navigate = useNavigate();
  const [firstLoad, setFirstLoad] = useState(0);
  const GetUserId = props.Login.userid;
  const classes = useStyles();
  const [filters, setFilters] = useState([]);
  const [filterOn, setFilterOn] = useState(false);
  const [externalApis, setExternalApis] = useState([]);
  const [originalExternalApis, setOriginalExternalApis] = useState([]);
  const [sortColumn, setSortColumn] = useState("name");
  const [sortOrder, setSortOrder] = useState("ASC");
  const [companies, setCompanies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dataService.getApis(GetUserId),
      dataService.getAllCompaniesByUserId(GetUserId)
    ]).then(([apisResult, companiesResult]) => {
      // Sort the result by name in ascending order
      apisResult.sort((a, b) => a.name.localeCompare(b.name));
      setExternalApis(apisResult);
      setOriginalExternalApis(apisResult);
      setCompanies(companiesResult || []);
    }).finally(() => {
      setLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (firstLoad === 0) {
    setFirstLoad(1);
  }

  const handleSorting = (column) => {
    let newOrder = "ASC";
    let newColumn = column;
    
    if (sortColumn === column) {
      // If clicking the same column, cycle through: ASC -> DESC -> no sort
      if (sortOrder === "ASC") {
        newOrder = "DESC";
      } else {
        // Clear sorting - return to original order
        newColumn = "";
        newOrder = "ASC";
        setSortColumn("");
        setSortOrder("ASC");
        setExternalApis([...originalExternalApis]);
        return;
      }
    }
    
    setSortColumn(newColumn);
    setSortOrder(newOrder);
    
    let tempRows = [...externalApis];
    tempRows.sort((a, b) => {
      let aVal = a[column];
      let bVal = b[column];
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal > bVal) return newOrder === "ASC" ? 1 : -1;
      if (aVal < bVal) return newOrder === "ASC" ? -1 : 1;
      return 0;
    });
    setExternalApis(tempRows);
  };

  const toggleFilter = () => {
    if (filterOn) {
      setFilters([]);
    }
    setFilterOn(!filterOn);
  };

  const updateFilters = (type, event) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === type)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === type));
      tempFilters.splice(index, 1);
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    } else {
      tempFilters.unshift({ type: type, filterValue: event.target.value });
    }
    setFilters(tempFilters);
    filterApi(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const updateCompanyFilter = (selectedCompany) => {
    let tempFilters = [...filters];
    if (tempFilters.some((x) => x.type === ExternalApiTypes.COMPANY)) {
      let index = tempFilters.indexOf(tempFilters.find((x) => x.type === ExternalApiTypes.COMPANY));
      tempFilters.splice(index, 1);
      if (selectedCompany) {
        tempFilters.unshift({ type: ExternalApiTypes.COMPANY, filterValue: selectedCompany.id });
      }
    } else {
      if (selectedCompany) {
        tempFilters.unshift({ type: ExternalApiTypes.COMPANY, filterValue: selectedCompany.id });
      }
    }
    setFilters(tempFilters);
    filterApi(tempFilters);
    setPage(0); // Reset to first page when filters change
  };

  const filterApi = (thisFilters) => {
    let varFilteredApi = [...originalExternalApis];
    var i;
    for (i = 0; i < thisFilters.length; i++) {
      let filter = thisFilters[i];
      switch (filter.type) {
        case ExternalApiTypes.NAME:
          varFilteredApi = varFilteredApi.filter((x) => x.name.toLowerCase().includes(filter.filterValue.toLowerCase()));
          break;
        case ExternalApiTypes.COMPANY:
          varFilteredApi = varFilteredApi.filter((x) => x.companyId === parseInt(filter.filterValue) || filter.filterValue === "");
          break;
        default:
          break;
      }
    }
    setExternalApis(varFilteredApi);
  };

  const getFilterValue = (type) => {
    let filter = filters.find((x) => x.type === type);

    if (filter) {
      return filter.filterValue;
    } else {
      return "";
    }
  };

  const navigateTo = (destination) => {
    navigate(destination, { state: { from: "/externalapioverview" } });
  };

  // Utility function to get selected company
  const getSelectedCompany = () => {
    const companyId = getFilterValue(ExternalApiTypes.COMPANY);
    return companies.find((company) => company.id === parseInt(companyId)) || null;
  };

  // Utility function to get search value
  const getSearchValue = () => {
    return getFilterValue(ExternalApiTypes.NAME);
  };

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  // Calculate paginated rows
  const paginatedRows = externalApis.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <React.Fragment>
      <TitlePage Text="Manage your APIs" backbutton={true} {...props} />
      <Box
        sx={{
          maxWidth: "90%",
          display: "flex",
          justifyContent: "center",
          flexDirection: "column",
          marginRight: "auto",
          marginLeft: "auto",
          marginTop: "148px",
        }}
      >
        <div className={classes.header}>
          <h1>Manage your API's</h1>
        </div>
        <div className={classes.tableManagementContainer}>
          <div className={classes.tableManagementContainer__start}>
            <div className={classes.searchContainer}>
              <input
                placeholder="Search for API's"
                className={classes.search__input}
                type="text"
                value={getFilterValue(ExternalApiTypes.NAME)}
                onChange={(e) => updateFilters(ExternalApiTypes.NAME, e)}
              />
              <Search size={16} className={classes.inputIcon} />
            </div>
            <div className={classes.filterContainer}>
              <Select value={getSelectedCompany()?.id} onChange={updateCompanyFilter} size="small">
                <SelectTrigger>{getSelectedCompany()?.name || "All Companies"}</SelectTrigger>
                <SelectContent>
                  <SelectItem value={null}>All Companies</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className={classes.tableManagementContainer__end}>
            <Button size="small" startIcon={<Plus />} onClick={() => navigateTo("/createapi")}>
              Create API
            </Button>
          </div>
        </div>
        <div className={classes.container}>
          <Grid2 container columnSpacing={3} rowSpacing={1} alignItems="center">
            <Grid2 item size={12}>
              <TableExternalApi
                {...props}
                toggleFilter={toggleFilter}
                filters={filters}
                handleSorting={handleSorting}
                sortColumn={sortColumn}
                sortOrder={sortOrder}
                filterOn={filterOn}
                filterApi={filterApi}
                updateFilters={updateFilters}
                rows={paginatedRows}
                searchValue={getSearchValue()}
                loading={loading}
              />
              <TablePagination
                component="div"
                count={externalApis.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </Grid2>
          </Grid2>
        </div>
      </Box>
    </React.Fragment>
  );
}

export default ExternalApiOverviewPage;

const useStyles = makeStyles({
  container: {
    boxSizing: "border-box",
    maxWidth: "100%",
    marginBottom: "48px",
  },

  header: {
    width: "100%",
    height: "40px",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",

    marginBottom: "16px",
  },



  tableManagementContainer: {
    width: "100%",
    height: "fit-content",

    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "8px",
    flexWrap: "wrap-reverse",

    marginBottom: "8px",
  },

  tableManagementContainer__start: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
    marginRight: "16px",
  },

  searchContainer: {
    width: "240px",
    position: "relative",
  },

  filterContainer: {
    width: "auto",
    minWidth: "fit-content",
  },

  search__input: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    paddingRight: "32px",

    borderRadius: "4px",
    border: "1px solid #DCDCDC",

    fontSize: "14px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",

    backgroundColor: "#FFFFFF",

    "&:focus": {
      outline: "none",
      border: "1px solid #C3996B",
    },
  },

  inputIcon: {
    color: "#0C0E29",
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    right: "8px",
  },

  tableManagementContainer__end: {
    width: "fit-content",

    display: "flex",
    alignItens: "center",
    gap: "8px",
  },


});
